import type { OnSelectCallbackParams } from '@/components/StructuredVirtualList'

import { useCallback } from 'react'

import { useReadOnly } from '@/hooks'
import { fitMapToSelection } from '@/map'

import { useDataForList } from './useDataForList'

export function useListHandlers(selection: workwave.DeepReadonly<string[]>) {
  const { readonly } = useReadOnly()
  const { structure, setEditing, deselectAll, setSelection } = useDataForList()

  const handleOnSelect = useCallback(
    (params: OnSelectCallbackParams<'regions'>) => {
      const regionIds: string[] = []

      for (const regionId of params.newSelectedIds) {
        if (regionId.startsWith('group')) continue

        regionIds.push(regionId)
      }

      setSelection(regionIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    },
    [setSelection],
  )

  const handleOnSelectAll = useCallback(() => {
    const allSelected = structure.shownItems <= selection.length

    const regionIds: string[] = []

    for (const listItem of structure.list) {
      if (typeof listItem === 'string') {
        regionIds.push(listItem)
      }
    }

    if (allSelected) {
      deselectAll()
    } else {
      setSelection(regionIds)

      // try to select it right away and
      // queue a pending fitMap request
      // it will be applied by the destination view when it opens
      fitMapToSelection({ preventIfVisible: true }, 'add')
    }
  }, [setSelection, selection, deselectAll, structure])

  const handleOnDoubleClick = useCallback(() => {
    if (readonly) return

    setEditing([...selection])
  }, [setEditing, readonly, selection])

  const handleOnClick = useCallback(() => {
    if (readonly) return
    deselectAll()
    setEditing([])
  }, [setEditing, readonly, deselectAll])

  return {
    handleOnSelect,
    handleOnSelectAll,
    handleOnDoubleClick,
    handleOnClick,
  }
}
